import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, AlertTriangle, Clock, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDaysLeft, formatTrialDate } from "@/lib/trialDate";
import { useTeacherEntitlements } from "@/hooks/use-teacher-entitlements";
import { useSubscriptionStatus } from "@/hooks/use-teacher-subscription";
import { ScheduledPurchaseBanner } from "@/components/teacher/ScheduledPurchaseBanner";

/**
 * The teacher-facing countdown for an active Platinum free trial (use cases 1-7). Patterned on
 * `GracePeriodBanner`: no props, gated on its own query, dismissible for the session.
 *
 * Three things this gets deliberately right, each of which is a test:
 *
 * 1. **Days left come from the server** (`trial.daysRemaining`), never from `endsAt` and the
 *    browser clock. The server's number is what the reminder emails and the expiry job use; a
 *    locally recomputed one drifts by a day across timezones and midnight.
 * 2. **It gates on server truth, not on a release flag.** The backend never consults
 *    TEACHER_PLATINUM_TRIAL when resolving a trial, so a teacher with a live trial must see it
 *    regardless of the flag. Before launch nobody has a trial, so the banner is inert anyway.
 * 3. **Urgency is never colour alone** (WCAG 1.4.1): every state carries a text label and an icon,
 *    and the urgent state escalates to `role="alert"`.
 *
 * It renders nothing -- no skeleton -- while entitlements load or if that fetch fails: a dashboard
 * must not shift its content for this, and a failed side-fetch must never block the page.
 */

/** 3 days or fewer: `role="alert"`, destructive treatment, pulse. Matches GracePeriodBanner's shape. */
const URGENT_WITHIN_DAYS = 3;
/**
 * 30 days or fewer: the warning treatment with an "Ending soon" pill. The reminder emails escalate
 * at 30/15/3/1; the banner collapses that into the mockup's three visual states (calm, ending soon,
 * urgent) rather than inventing two more that look almost the same.
 */
const ENDING_SOON_WITHIN_DAYS = 30;

/** Dismissal is per browser session AND per trial end date, so a re-granted trial shows again. */
function dismissKeyFor(endsAt: string): string {
  return `teacher_trial_banner_dismissed_${endsAt}`;
}

export function TrialCountdownBanner() {
  const headingId = useId();
  const { data: entitlements, isError } = useTeacherEntitlements();

  const trial = entitlements?.trial ?? null;
  const trialActive = Boolean(trial?.active);

  // Only asked for once we know there is a trial -- most dashboard loads never need it.
  const { data: status } = useSubscriptionStatus(trialActive);
  const scheduled = status?.scheduledSubscription ?? null;

  const endsAt = trial?.endsAt;
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!endsAt) return;
    try {
      setDismissed(sessionStorage.getItem(dismissKeyFor(endsAt)) === "true");
    } catch {
      setDismissed(false);
    }
  }, [endsAt]);

  useEffect(() => {
    if (isError) {
      console.warn("TrialCountdownBanner: could not load teacher entitlements; hiding the trial notice.");
    }
  }, [isError]);

  if (!trial || !trialActive) return null;

  // A teacher who has already bought their next tier gets the confirmation, not a countdown.
  if (scheduled) return <ScheduledPurchaseBanner scheduled={scheduled} trialEndsAt={trial.endsAt} />;

  if (dismissed) return null;

  const daysLeft = formatDaysLeft(trial.daysRemaining);
  const isUrgent = trial.daysRemaining <= URGENT_WITHIN_DAYS;
  const isEndingSoon = !isUrgent && trial.daysRemaining <= ENDING_SOON_WITHIN_DAYS;
  const Icon = isUrgent ? AlertOctagon : isEndingSoon ? AlertTriangle : Sparkles;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(dismissKeyFor(trial.endsAt), "true");
    } catch {
      // Session-only convenience -- safe to no-op if storage is unavailable.
    }
  };

  return (
    <div
      role={isUrgent ? "alert" : "status"}
      aria-labelledby={headingId}
      className={cn(
        "rounded-2xl p-5 transition-colors duration-200",
        isUrgent
          ? "border-2 border-rose-500 bg-rose-50 motion-safe:animate-pulse"
          : isEndingSoon
            ? "border-2 border-amber-300 bg-amber-50"
            : "border border-violet-200 bg-violet-50"
      )}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={cn(
            "w-5 h-5 shrink-0 mt-0.5",
            isUrgent ? "text-rose-700" : isEndingSoon ? "text-amber-700" : "text-violet-700"
          )}
          aria-hidden="true"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isUrgent ? (
              <>
                <span
                  id={headingId}
                  className="inline-flex items-center text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-rose-200 border border-rose-400 text-rose-900"
                >
                  Urgent — {daysLeft}
                </span>
                <p className="text-sm font-bold text-rose-900">Your Platinum trial is about to end</p>
              </>
            ) : (
              <>
                <p
                  id={headingId}
                  className={cn("text-sm font-bold", isEndingSoon ? "text-amber-900" : "text-violet-900")}
                >
                  Platinum trial — {daysLeft}
                </p>
                {isEndingSoon && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-200 border border-amber-400 text-amber-900">
                    <Clock className="w-3 h-3" aria-hidden="true" />
                    Ending soon
                  </span>
                )}
              </>
            )}
          </div>

          <p
            className={cn(
              "text-sm mt-1",
              isUrgent ? "text-rose-800" : isEndingSoon ? "text-amber-800" : "text-violet-800"
            )}
          >
            Ends {formatTrialDate(trial.endsAt)}. After that you keep 1 one-to-one offering unless you
            subscribe.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <Button
            asChild
            size="sm"
            variant="link"
            className={cn(
              "cursor-pointer font-semibold transition-colors duration-200",
              isUrgent ? "text-rose-900" : isEndingSoon ? "text-amber-900" : "text-violet-900"
            )}
          >
            <Link to="/teacher-subscription">See plans</Link>
          </Button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss trial reminder"
            className={cn(
              "w-6 h-6 flex items-center justify-center rounded shrink-0 cursor-pointer transition-colors duration-200",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
              isUrgent
                ? "text-rose-700 hover:text-rose-900 hover:bg-rose-900/10 focus-visible:ring-rose-600"
                : isEndingSoon
                  ? "text-amber-700 hover:text-amber-900 hover:bg-amber-900/10 focus-visible:ring-amber-600"
                  : "text-violet-700 hover:text-violet-900 hover:bg-violet-900/10 focus-visible:ring-violet-600"
            )}
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
